/**
 * Settings → Profil: the person's own name and their login (phone, e-mail).
 *
 * The name is one form with one explicit save (Primer: text fields save explicitly, one save per
 * form, labelled with what it saves). Phone and e-mail change through the verified-change dialog
 * (#2008d), never inline — each is a login.
 *
 * While a HofSync admin impersonates ("Anzeige als", HOF-708) the pane shows the person being
 * viewed — ``impersonated_person`` from ``/auth/me``, never the admin's own identity fields — and
 * every action here acts on that person, exactly as they would: the same routes carry the signed
 * impersonation context and the backend resolves the viewed person (``identity_person``).
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BadgeCheck } from 'lucide-react';
import { Badge, Button, Input, SettingsPane, SettingsRow, SettingsSection } from '@hofsync/design-system';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import api from '../../services/api';
import CredentialChangeModal, { type CredentialType } from '../../components/CredentialChangeModal';
import type { SettingsPaneChrome } from './paneChrome';

export default function ProfilePane({ backLink, focusOnMount }: SettingsPaneChrome) {
    const { t } = useTranslation();
    const { userProfile, fetchUserProfile, impersonation } = useAuth();
    const viewing = impersonation?.active === true;
    // The person this pane is about: the viewed one while impersonating, else the signed-in one.
    const person = viewing ? (userProfile?.impersonated_person ?? null) : userProfile;
    const { showToast } = useToast();
    const [form, setForm] = useState({ first_name: '', last_name: '' });
    const [saving, setSaving] = useState(false);
    const [changeModal, setChangeModal] = useState<CredentialType | null>(null);

    useEffect(() => {
        if (person) {
            setForm({ first_name: person.first_name || '', last_name: person.last_name || '' });
        }
    }, [person]);

    const save = async () => {
        setSaving(true);
        try {
            await api.patch('/v1/auth/profile', form, { withCredentials: true });
            await fetchUserProfile();
            showToast(t('settings.profileSaved', 'Profil gespeichert'), 'success');
        } catch (error) {
            console.error('Failed to save profile:', error);
            showToast(t('settings.profileSaveFailed', 'Profil konnte nicht gespeichert werden'), 'error');
        } finally {
            setSaving(false);
        }
    };

    const credentialChanged = async () => {
        setChangeModal(null);
        await fetchUserProfile();
        showToast(t('settings.contact.changeSuccess', 'Änderung gespeichert'), 'success');
    };

    const change = t('settings.contact.change', 'Ändern');
    const title = t('settings.profile.title', 'Profil');
    const description = viewing
        ? t('settings.profile.viewingSubtitle', 'Persönliche Daten der Person, als die du den Betrieb gerade ansiehst')
        : t('settings.profile.subtitle', 'Deine persönlichen Daten');

    if (viewing && !person) {
        // A backend that predates HOF-708 sends no viewed person: never show the admin's own data instead.
        return (
            <SettingsPane title={title} description={description} backLink={backLink} focusOnMount={focusOnMount}>
                <p role="status">
                    {t('settings.profile.viewingUnavailable', 'Das Profil der angezeigten Person ist nicht verfügbar.')}
                </p>
            </SettingsPane>
        );
    }

    return (
        <SettingsPane title={title} description={description} backLink={backLink} focusOnMount={focusOnMount}>
            <SettingsSection
                title={t('settings.profile.nameTitle', 'Name')}
                action={
                    <Button size="sm" onClick={save} disabled={saving}>
                        {t('settings.profile.save', 'Profil speichern')}
                    </Button>
                }
            >
                <SettingsRow label={t('settings.profile.firstName', 'Vorname')} htmlFor="profile-first-name" field>
                    <Input
                        id="profile-first-name"
                        value={form.first_name}
                        onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))}
                        autoComplete="given-name"
                    />
                </SettingsRow>
                <SettingsRow label={t('settings.profile.lastName', 'Nachname')} htmlFor="profile-last-name" field>
                    <Input
                        id="profile-last-name"
                        value={form.last_name}
                        onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))}
                        autoComplete="family-name"
                    />
                </SettingsRow>
            </SettingsSection>

            <SettingsSection
                title={t('settings.contact.title', 'Kontakt & Anmeldung')}
                description={t('settings.contact.subtitle', 'Deine Anmeldedaten für die Anmeldung')}
            >
                <SettingsRow label={t('settings.contact.phone', 'Telefon')} description={person?.phone || '—'}>
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setChangeModal('phone')}
                        aria-label={`${t('settings.contact.phone', 'Telefon')} ${change.toLowerCase()}`}
                    >
                        {change}
                    </Button>
                </SettingsRow>
                <SettingsRow
                    label={t('settings.contact.email', 'E-Mail')}
                    description={
                        person?.email ? (
                            <>
                                <span>{person.email}</span>{' '}
                                {person.email_verified_at && (
                                    <Badge tone="success">
                                        <BadgeCheck aria-hidden="true" size={12} />
                                        {t('settings.contact.verified', 'Bestätigt')}
                                    </Badge>
                                )}
                            </>
                        ) : (
                            t('settings.contact.noEmail', 'Nicht hinterlegt')
                        )
                    }
                >
                    <Button size="sm" variant="outline" onClick={() => setChangeModal('email')}>
                        {person?.email ? change : t('settings.contact.add', 'Hinzufügen')}
                    </Button>
                </SettingsRow>
            </SettingsSection>

            {changeModal && (
                <CredentialChangeModal
                    credentialType={changeModal}
                    onClose={() => setChangeModal(null)}
                    onSuccess={credentialChanged}
                />
            )}
        </SettingsPane>
    );
}
